import React from 'react';
import { Award, Globe, Languages, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useSectionsContent } from '../hooks/use-sections-content';
import { useLanguage } from '../i18n/LanguageContext';

const About = () => {
  const { sectionsContent } = useSectionsContent();
  const { tr, localizeText } = useLanguage();

  const about = sectionsContent?.about || {};
  const coreValues = Array.isArray(sectionsContent?.coreValues)
    ? sectionsContent.coreValues
    : [];

  const valueIcons = [CheckCircle, Award, Globe];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative h-96 flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src={about.image}
            alt={localizeText(about.title) || tr('nav.about')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sage-900/85 to-sage-800/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl font-bold text-white mb-4">{localizeText(about.title)}</h1>
          <p className="text-xl text-sage-100">{localizeText(about.subtitle)}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-sage-900 mb-6">{tr('about.approachTitle')}</h2>
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                {localizeText(about.bio)}
              </p>
              <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                {localizeText(about.approachParagraph)}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <Card className="border-sage-200 bg-sage-50">
                  <CardContent className="p-6">
                    <Languages className="w-8 h-8 text-sage-600 mb-3" />
                    <h3 className="font-semibold text-sage-900 mb-2">{tr('about.languages')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(about.languages) ? about.languages : []).map((lang, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-sage-100 text-sage-700">
                          {localizeText(lang)}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-sage-200 bg-sage-50">
                  <CardContent className="p-6">
                    <Globe className="w-8 h-8 text-sage-600 mb-3" />
                    <h3 className="font-semibold text-sage-900 mb-2">{tr('about.serviceAreas')}</h3>
                    <p className="text-sm text-sage-700">
                      {localizeText(about.serviceAreaLine1)}<br />
                      {localizeText(about.serviceAreaLine2)}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div>
              <Card className="border-sage-200 shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-sage-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-sage-900">{tr('about.qualifications')}</h2>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {(Array.isArray(about.credentials) ? about.credentials : []).map((credential, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sage-700">
                        <CheckCircle className="w-5 h-5 text-sage-500 mt-0.5 flex-shrink-0" />
                        <span className="leading-relaxed">{localizeText(credential)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-sage-50 rounded-lg p-6 border border-sage-200">
                    <h3 className="font-semibold text-sage-900 mb-3">{tr('about.commitmentTitle')}</h3>
                    <p className="text-sage-700 leading-relaxed">
                      {localizeText(about.commitmentText)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-sage-200 shadow-lg bg-white mt-6">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-sage-900 mb-4">{localizeText(about.philosophyTitle)}</h3>
                  <p className="text-sage-700 leading-relaxed mb-4">
                    {localizeText(about.philosophyParagraph1)}
                  </p>
                  <p className="text-sage-700 leading-relaxed">
                    {localizeText(about.philosophyParagraph2)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-sage-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-sage-900 mb-12 text-center">{tr('about.coreValuesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = valueIcons[index] || CheckCircle;
              return (
                <Card key={`${localizeText(value.title)}-${index}`} className="border-sage-200 bg-white hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-sage-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-sage-900 mb-3">{localizeText(value.title)}</h3>
                    <p className="text-sage-700 leading-relaxed">
                      {localizeText(value.description)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
